import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';
import clsx from 'clsx';

import useFormik from 'hooks/useFormik';
import { CustomFormControl } from 'components/Form/FormControl';
import ActionRow from 'components/ActionRow';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';

import { UserProfileUploadAvatar } from '../UserProfileUploadAvatar';
import { validationSchema } from './validationSchema';
import styles from './styles.module.scss';
import { useState } from 'react';
import userService from '../../../../store/user/service';
import { UserRoleEnum } from '../../../../common/user/enums/user-role.enum';
import { useManager } from '../../../../contexts/access/ManagerContext/useManager';

export type IUserProfile = {
  _id?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  avatar?: string;
  roles?: UserRoleEnum[];
  organizationId?: string;
};

type InputProps = {
  user: IUserProfile;
  onSubmit: (user: IUserProfile, avatar?: File | Blob) => void;
};

export const UserProfileForm = ({ user, onSubmit }: InputProps) => {
  const { t } = useTranslation();
  const [newAvatar, setNewAvatar] = useState<File | Blob>();
  const [avatarUrl, setAvatarUrl] = useState<string>(user.avatar ?? '');
  const [updateAvatar] = userService.endpoints.uploadAvatarUser.useMutation();
  const { isReadOnly } = useManager();

  const { getFieldProps, getFieldErrorProp, handleSubmit } = useFormik({
    initialValues: user,
    validationSchema,
    onSubmit(value: IUserProfile) {
      onSubmit(value, newAvatar || undefined);
    },
  });

  const handleChangeAvatar = (file: File | Blob) => {
    setNewAvatar(file);
    setAvatarUrl(file ? URL.createObjectURL(file) : '');
    updateAvatar({ file });
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={clsx(styles.row, styles.mb)}>
        <UserProfileUploadAvatar
          avatarUrl={avatarUrl}
          onChange={handleChangeAvatar}
        />
      </div>
      <div className={clsx(styles.row, styles.mb)}>
        <CustomFormControl
          error={getFieldErrorProp('firstName').error}
          label={t('UserProfileForm.label.firstName')}
          required={true}
          inputProps={getFieldProps('firstName')}
        />
        <CustomFormControl
          error={getFieldErrorProp('lastName').error}
          label={t('UserProfileForm.label.lastName')}
          required={true}
          inputProps={getFieldProps('lastName')}
        />
      </div>
      <div className={styles.row}>
        <CustomFormControl
          error={getFieldErrorProp('email').error}
          label={t('UserProfileForm.label.email')}
          required={true}
          inputProps={{
            disabled: true,
            value: getFieldProps('email').value,
          }}
        />
        <CustomFormControl
          label={t('UserProfileForm.label.position')}
          required={false}
          inputProps={{
            disabled: true,
            value: isReadOnly ? 'Read Only' : 'Compliance Manager',
          }}
        />
      </div>
      <Divider />
      <ActionRow style="right" marginTop={false} className={styles.actions}>
        <ButtonWithArrowIcon type="submit">
          {t('UserProfileForm.btn.save')}
        </ButtonWithArrowIcon>
      </ActionRow>
    </form>
  );
};
